import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Lock } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_LABELS = {
  invoices: 'invoices', bills: 'bills', other_receipts: 'receipts', other_payments: 'payments',
  loans: 'loans', fixed_assets: 'fixed assets', investments: 'investments',
  prepayment_schedules: 'prepayments', prepayments: 'prepayments', prepaid_expenses: 'prepaid expenses',
  deferred_revenue: 'deferred revenue', deferred_revenue_schedules: 'deferred revenue',
  deferral_schedules: 'deferrals', accruals: 'accruals', accrued_expenses: 'accrued expenses', contacts: 'contacts',
};

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between py-1.5 text-sm" data-testid={testId}>
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium tabular-nums">{value}</span>
  </div>
);

export const RemoveBankAccountDialog = ({ bank, open, onOpenChange, onStarted }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !bank?.id) return;
    setPreview(null); setError(null); setPassword(''); setReason(''); setLoading(true);
    axios.get(`${API}/bank-accounts/${bank.id}/removal-preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Could not load removal preview'))
      .finally(() => setLoading(false));
  }, [open, bank?.id]);

  const submit = async () => {
    if (!password) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/bank-accounts/${bank.id}/remove`, { password, reason: reason || null });
      toast.success(r.data?.message || 'Removal started');
      onOpenChange(false);
      onStarted?.(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : d?.message || 'Failed to remove bank account';
      if (d?.code === 'incorrect_password') toast.error('Incorrect password.');
      else toast.error(msg, { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  const s = preview?.summary;
  const docs = s?.documents || {};
  const blocked = preview && !preview.can_remove;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="remove-bank-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            Remove {bank?.bank_name || 'bank account'}{bank?.mask ? ` ····${bank.mask}` : ''}
          </DialogTitle>
          <DialogDescription>
            Everything posted through this account is taken out of your books — bank lines, journals,
            documents created from its feed, their schedules and any payments it settled. You can restore
            it later from “Removed bank accounts”.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground" data-testid="remove-preview-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Working out what will be removed…
          </div>
        )}
        {error && <p className="text-sm text-red-600" data-testid="remove-preview-error">{String(error)}</p>}

        {s && (
          <div className="rounded-lg border bg-muted/30 px-4 py-2 divide-y" data-testid="remove-impact-summary">
            <Row label="Bank feed lines" value={s.feed_lines} testId="impact-feed-lines" />
            <Row label="Statement uploads" value={s.uploads} testId="impact-uploads" />
            <Row label="Journal entries (opening balance, feed, transfers, lifecycle)" value={s.journals} testId="impact-journals" />
            {Object.entries(docs).map(([k, v]) => (
              <Row key={k} label={`Created ${DOC_LABELS[k] || k}`} value={v} testId={`impact-doc-${k}`} />
            ))}
            {s.schedules > 0 && <Row label="Depreciation / amortisation / loan schedule rows" value={s.schedules} testId="impact-schedules" />}
            {s.payments_unwound > 0 && (
              <Row label="Payments unwound on existing invoices / bills" value={`${s.payments_unwound}`} testId="impact-payments-unwound" />
            )}
            {s.other_bank_rows_released > 0 && (
              <Row label="Other-bank lines sent back to Review" value={s.other_bank_rows_released} testId="impact-released-rows" />
            )}
            {s.first_journal_date && (
              <Row label="Period affected" value={`${s.first_journal_date} → ${s.last_journal_date}`} testId="impact-period" />
            )}
            {preview.account?.connected && (
              <p className="py-2 text-xs text-amber-700" data-testid="impact-connected-note">
                This account is connected — the bank link will be disconnected. Restoring will ask you to reconnect.
              </p>
            )}
          </div>
        )}

        {blocked && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="remove-locked-periods">
            <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Entries fall in closed periods: <b>{preview.locked_periods.join(', ')}</b>. Reopen them first, then try again.</span>
          </div>
        )}

        {preview && !blocked && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="remove-reason">Reason (optional)</Label>
              <Input id="remove-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. account belongs to another entity" data-testid="remove-reason-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="remove-password">Confirm your password</Label>
              <Input id="remove-password" type="password" autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="remove-password-input" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="remove-cancel-btn">Cancel</Button>
          <Button variant="destructive" disabled={!preview || blocked || !password || submitting}
            onClick={submit} data-testid="remove-confirm-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Remove bank account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RemoveBankAccountDialog;
